import { useTranslation } from "@shared/utils/internationalization";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Text from "@ui-kit/typography/Text/Text.web";
import type { RemoveAllSeatsModalSharedProps } from "./RemoveAllSeatsModal.props";
import { useRemoveAllSeatsModal } from "./useRemoveAllSeatsModal";

export default function RemoveAllSeatsModal(
  props: RemoveAllSeatsModalProps
): JSX.Element {
  const { t } = useTranslation("seating");

  const { isOpen, $data } = props;
  const { cancel, removeAllSeats, seatingHref } = useRemoveAllSeatsModal(props);
  const headerText = t("Section {{section}}, Row {{row}}, Seat {{seat}}", {
    section: $data.segment_identifier,
    row: $data.location_identifier,
    seat: $data.identifier,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={cancel}
      size="3xl"
      isCentered
      scrollBehavior="inside"
    >
      <Modal.Header>
        <Flex alignItems="center" data-testid="default-modal-title">
          {headerText}
        </Flex>
      </Modal.Header>

      <Modal.Body>
        <Flex
          data-testid="remove-all-seats-modal"
          w="full"
          direction="column"
          gap="md"
        >
          <Text fontSize="xl" fontWeight="bold">
            {t("Additional Actions Required")}
          </Text>

          <Divider my="2xs" />

          <Text fontWeight="bold">
            {t(
              "Removing this seat requires one or more seats to also be removed."
            )}
          </Text>

          <Text fontWeight="normal">
            {t(
              "This seat may be required for a full table purchase or to prevent empty seats in the row."
            )}
          </Text>

          <Text fontWeight="normal">
            <span>{t("Please remove all tickets for this event ")}</span>
            <strong>{t("OR")}</strong>
            <span> {t(" to change your seats, ")}</span>
            <Link
              href={seatingHref}
              ml="none"
              variant="underline"
              color="secondary.600"
            >
              <strong>{t("CLICK HERE")}</strong>
            </Link>
          </Text>
        </Flex>
      </Modal.Body>

      <Modal.Footer>
        <Button.Solid
          colorPalette="primary"
          onClick={removeAllSeats}
          data-testid="remove-all-seats-confirm"
        >
          {t("Remove")}
        </Button.Solid>

        <Button.Ghost onClick={cancel} data-testid="remove-all-seats-close">
          {t("Close", { ns: "common" })}
        </Button.Ghost>
      </Modal.Footer>
    </Modal>
  );
}

export type RemoveAllSeatsModalProps = RemoveAllSeatsModalSharedProps & {
  // Add any web specific props here.
};
